/**
 * ScriptList — the sparse choreography timer of the particles-optimised type
 * (stage 4). Replaces the reference's per-particle ParticleCounter closures
 * (and the choreography setTimeouts around them): instead of ~8000 counter
 * behaviours ticked per particle per frame, a SMALL array of pending entries
 * is ticked ONCE per frame, outside the kernels.
 *
 * Semantics mirror ParticleCounter.tick() exactly:
 *  - counting is FRAME-normalised time, not wall-clock: count accumulates
 *    window.deltaTime (the app kernel's 60fps-normalised channel), and fires
 *    at count >= triggerAtFrames — so scripts freeze with the tick loop,
 *    like DelayHelper timers and unlike native setTimeout;
 *  - on fire: count resets, non-loop entries are removed BEFORE onTrigger
 *    runs (so a trigger can safely re-schedule), loop entries keep firing
 *    every triggerAtFrames.
 *
 * millisecond helpers use ParticleCounter.millsecsToFrames' conversion
 * (ms * 60 / 1000). Where the reference used native setTimeout for intro
 * choreography (IntroParticleProcessor.introComplete's fade-outs), the
 * frame-domain conversion is deliberate: identical at 60fps and it keeps the
 * choreography pausable — the one intended deviation.
 *
 * `laneIndex` is carried on the entry and passed to onTrigger so per-particle
 * triggers write lanes without per-particle closures capturing state.
 */

export type ScriptTrigger = (laneIndex: number) => void;

interface ScriptEntry {
	laneIndex: number;
	triggerAtFrames: number;
	count: number;
	loop: boolean;
	group: string | null;
	onTrigger: ScriptTrigger;
}

export class ScriptList {
	private _entries: Array<ScriptEntry> = [];

	/** Number of pending entries (probe/testing hook). */
	public get size(): number {
		return this._entries.length;
	}

	public add(laneIndex: number, triggerAtFrames: number, onTrigger: ScriptTrigger, loop: boolean = false, group: string | null = null): void {
		this._entries.push({
			laneIndex: laneIndex,
			triggerAtFrames: triggerAtFrames,
			count: 0,
			loop: loop,
			group: group,
			onTrigger: onTrigger
		});
	}

	/** ParticleCounter.millsecsToFrames conversion: frames = ms * 60 / 1000. */
	public addMs(laneIndex: number, ms: number, onTrigger: ScriptTrigger, loop: boolean = false, group: string | null = null): void {
		this.add(laneIndex, (ms * 60) / 1000, onTrigger, loop, group);
	}

	/** Cancel every pending entry in a group (the reference's per-particle
	 *  `counter.active = false` sweep, e.g. intro introComplete). */
	public cancelGroup(group: string): void {
		let i = 0;
		while (i < this._entries.length) {
			if (this._entries[i].group === group) {
				this._entries.splice(i, 1);
			} else {
				i++;
			}
		}
	}

	public clear(): void {
		this._entries = [];
	}

	/** Tick once per frame at choreography frequency (NOT per particle). */
	public tick(deltaTime: number): void {
		let i = 0;
		while (i < this._entries.length) {
			const entry = this._entries[i];
			entry.count += deltaTime;

			if (entry.count >= entry.triggerAtFrames) {
				entry.count = 0;

				//Remove-before-fire so onTrigger can re-schedule safely
				//(mirrors ParticleCounter: active=false precedes onTrigger):
				if (!entry.loop) {
					this._entries.splice(i, 1);
				} else {
					i++;
				}

				entry.onTrigger(entry.laneIndex);
			} else {
				i++;
			}
		}
	}
}
